import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  Briefcase,
  CheckCircle,
  FileText,
  Package,
  ShieldCheck,
  AlertTriangle,
  Calendar,
  MapPin,
  User,
  ChevronRight,
  Rocket,
  Send,
  Eye,
  CheckCheck,
  PlaneTakeoff,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Progress } from '@/app/components/ui/progress';
import { useMyDashboard, useMyComplianceStanding } from '@/hooks/use-my-dashboard';
import type { MyJSA, MyJob, MyCert } from '@/hooks/use-my-dashboard';
import { useMyJobPackets } from '@/hooks/use-my-job-packets';
import { useMyTravel } from '@/hooks/use-my-travel';
import type { MyJobPacket } from '@/lib/types';
import { useNotifications, useMarkNotificationsRead } from '@/hooks/use-platform';
import { useOnboardingState } from '@/hooks/use-onboarding';
import { usePlatformModules } from '@/hooks/use-modules';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';

function CardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-5 w-24" />
      </CardHeader>
      <CardContent className="space-y-3">
        <Skeleton className="h-8 w-16" />
        <Skeleton className="h-4 w-32" />
      </CardContent>
    </Card>
  );
}

function PendingJSAItem({ jsa }: { jsa: MyJSA }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-accent transition-colors">
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 truncate">{jsa.title}</h4>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
          {jsa.location && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {jsa.location}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {formatDateOnly(jsa.date)}
          </span>
          <span className="flex items-center gap-1">
            <User className="h-3.5 w-3.5" />
            {jsa.created_by_username}
          </span>
        </div>
      </div>
      <Button asChild size="lg" className="w-full sm:w-auto min-h-[44px] min-w-[120px]">
        <Link to={`/safety/jsas/${jsa.id}`}>
          Sign Now
          <ChevronRight className="ml-1 h-4 w-4" />
        </Link>
      </Button>
    </div>
  );
}

function JobItem({ job }: { job: MyJob }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-accent transition-colors">
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 truncate">{job.project_name || 'Unnamed Project'}</h4>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
          {job.project_location && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {job.project_location}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {formatDateOnly(job.start_date)}
            {job.end_date && ` — ${formatDateOnly(job.end_date)}`}
          </span>
          {job.role && (
            <span className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              {job.role}
            </span>
          )}
          {job.supervisor && (
            <span className="text-xs text-gray-400">Supervisor: {job.supervisor}</span>
          )}
        </div>
      </div>
      <ChevronRight className="hidden sm:block h-5 w-5 text-gray-300 flex-shrink-0" />
    </div>
  );
}

function CertItem({ cert }: { cert: MyCert }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-accent transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-gray-900 truncate">{cert.cert_name || cert.cert_type}</h4>
          {cert.expiry_warning === 'red' && (
            <Badge variant="destructive" className="text-xs">Expired</Badge>
          )}
          {cert.expiry_warning === 'amber' && (
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs">
              Expiring Soon
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
          {cert.issuing_body && <span>{cert.issuing_body}</span>}
          {cert.cert_number && <span>#{cert.cert_number}</span>}
          {cert.expiry_date && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              Expires: {formatDateOnly(cert.expiry_date)}
              {cert.days_until_expiry !== null && cert.days_until_expiry >= 0 && (
                <span className="text-xs">({cert.days_until_expiry} days)</span>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const PACKET_STATUS_CONFIG: Record<string, { icon: React.ComponentType<{ className?: string }>; label: string; color: string }> = {
  sent: { icon: Send, label: 'Sent', color: 'bg-blue-100 text-blue-800' },
  viewed: { icon: Eye, label: 'Viewed', color: 'bg-amber-100 text-amber-800' },
  acknowledged: { icon: CheckCheck, label: 'Acknowledged', color: 'bg-green-100 text-green-800' },
};

function JobPacketItem({ packet }: { packet: MyJobPacket }) {
  const orgTimeZone = useOrgTimeZone();
  const cfg = PACKET_STATUS_CONFIG[packet.status] ?? PACKET_STATUS_CONFIG.sent;
  const StatusIcon = cfg.icon;
  return (
    <Link
      to={`/my-dashboard/job-packets/${packet.id}`}
      className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-accent transition-colors"
    >
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 truncate">
          {packet.project_name || 'Unnamed Project'}
          {packet.project_number ? ` (${packet.project_number})` : ''}
        </h4>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
          {packet.location && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {packet.location}
            </span>
          )}
          {packet.role && (
            <span className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              {packet.role}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {formatDateTimeInTimeZone(packet.sent_at, orgTimeZone)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge className={cfg.color}>
          <StatusIcon className="h-3 w-3 mr-1" />
          {cfg.label}
        </Badge>
        <ChevronRight className="h-5 w-5 text-gray-300 flex-shrink-0" />
      </div>
    </Link>
  );
}

function JobPacketsSection() {
  const { data: packets, isLoading } = useMyJobPackets();

  if (isLoading) {
    return (
      <Card id="job-packets">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-indigo-500" />
            H3Kit Job Packets
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!packets || packets.length === 0) return null;

  const pending = packets.filter((p) => p.status !== 'acknowledged');
  const acknowledged = packets.filter((p) => p.status === 'acknowledged');

  return (
    <Card id="job-packets">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="h-5 w-5 text-indigo-500" />
          H3Kit Job Packets
          {pending.length > 0 && (
            <Badge variant="destructive" className="ml-auto text-xs">
              {pending.length} pending
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Dispatched work packages from your supervisor
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {pending.map((p) => (
          <JobPacketItem key={p.id} packet={p} />
        ))}
        {acknowledged.length > 0 && pending.length > 0 && (
          <div className="pt-2 border-t">
            <p className="text-xs text-gray-400 mb-2">Acknowledged</p>
          </div>
        )}
        {acknowledged.map((p) => (
          <JobPacketItem key={p.id} packet={p} />
        ))}
      </CardContent>
    </Card>
  );
}

function TravelItinerariesSection() {
  const orgTimeZone = useOrgTimeZone();
  const { data: itineraries, isLoading } = useMyTravel();

  if (isLoading) {
    return (
      <Card id="travel-itineraries">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PlaneTakeoff className="h-5 w-5 text-sky-500" />
            Travel Itineraries
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!itineraries || itineraries.length === 0) return null;

  return (
    <Card id="travel-itineraries">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PlaneTakeoff className="h-5 w-5 text-sky-500" />
          Travel Itineraries
        </CardTitle>
        <CardDescription>Confirm your travel plans and review upcoming segments</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {itineraries.map((itinerary) => (
          <Link
            key={itinerary.id}
            to={`/my-dashboard/travel/${itinerary.id}`}
            className="flex flex-col gap-2 rounded-xl border p-4 hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <h4 className="truncate font-medium text-slate-900">{itinerary.project_name}</h4>
              <p className="text-sm text-slate-500">
                {itinerary.assignment_summary.location || itinerary.assignment_summary.location_fk_name || '—'}
                {' · '}
                {itinerary.segments.length} segment{itinerary.segments.length !== 1 ? 's' : ''}
              </p>
              {itinerary.acknowledged_at && (
                <p className="text-xs text-slate-400">
                  Confirmed {formatDateTimeInTimeZone(itinerary.acknowledged_at, orgTimeZone)}
                </p>
              )}
            </div>
            <Badge
              className={
                itinerary.is_confirmed
                  ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-amber-100 text-amber-800'
              }
            >
              {itinerary.is_confirmed ? 'Confirmed' : 'Unconfirmed'}
            </Badge>
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

function ComplianceStandingSection() {
  const { isModuleEnabled } = usePlatformModules();
  const complianceEnabled = isModuleEnabled('compliance');
  const { data, isLoading } = useMyComplianceStanding(complianceEnabled);

  if (!complianceEnabled) return null;

  if (isLoading) {
    return (
      <Card id="compliance-standing">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-sky-500" />
            Compliance Standing
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { summary } = data;
  const attention = summary.expiring_soon + summary.expired + summary.revoked;

  return (
    <Card id="compliance-standing">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-sky-500" />
          Compliance Standing
          {attention > 0 && (
            <Badge variant="destructive" className="ml-auto text-xs">
              {attention} to review
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Your certifications feeding QP-300 clause {data.clause_ref} &mdash; {data.clause_title}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
            <p className="text-2xl font-bold text-emerald-900">{summary.valid}</p>
            <p className="text-xs font-medium text-emerald-800">Valid</p>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-2xl font-bold text-amber-900">{summary.expiring_soon}</p>
            <p className="text-xs font-medium text-amber-800">Expiring soon</p>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-2xl font-bold text-red-900">{summary.expired}</p>
            <p className="text-xs font-medium text-red-800">Expired</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-2xl font-bold text-gray-700">{summary.revoked}</p>
            <p className="text-xs font-medium text-gray-600">Revoked</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function MyDashboardPage() {
  const orgTimeZone = useOrgTimeZone();
  const { data, isLoading, isError } = useMyDashboard();
  const { data: notificationsData, isLoading: notificationsLoading } = useNotifications();
  const { data: onboardingData } = useOnboardingState();
  const markReadMutation = useMarkNotificationsRead();
  const navigate = useNavigate();
  const location = useLocation();

  // The summary tiles link to in-page anchors (e.g. #action-required). A
  // client-side navigation only changes the URL, so scroll the target into
  // view ourselves once the dashboard data (and therefore the section) is
  // rendered. location.key makes clicking the same tile again re-scroll.
  useEffect(() => {
    if (!data || !location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [data, location.hash, location.key]);

  const notifications = notificationsData?.notifications ?? [];
  const unreadNotifications = notifications.filter((n) => !n.is_read);

  const showOnboardingBanner =
    onboardingData && !onboardingData.onboarding_completed;

  const handleNotificationClick = (link: string, id: number) => {
    markReadMutation.mutate({ ids: [id] });
    if (link) navigate(link);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Dashboard</h1>
        <p className="text-gray-600 mt-1">Your assignments, safety documents, gear, and certifications.</p>
      </div>

      {/* Onboarding resume banner */}
      {showOnboardingBanner && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Rocket className="h-5 w-5 shrink-0 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Your setup isn&apos;t complete yet</p>
                <p className="text-sm text-amber-700">
                  {onboardingData.completed_count} of {onboardingData.total_count} steps
                  done &mdash; pick up where you left off.
                </p>
              </div>
            </div>
            <Button asChild size="sm" className="shrink-0 bg-amber-600 hover:bg-amber-700">
              <Link to="/onboarding">Resume Setup</Link>
            </Button>
          </div>
          <Progress value={onboardingData.progress} className="mt-3 h-2" />
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="py-8 text-center">
            <AlertTriangle className="h-8 w-8 text-orange-500 mx-auto mb-2" />
            <p className="text-gray-600">Unable to load dashboard data</p>
          </CardContent>
        </Card>
      ) : data ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* My Projects Card */}
            <Link to="/my-dashboard#projects" className="block">
              <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-blue-500" />
                    My Projects
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-gray-900">{data.my_jobs_count}</p>
                  <p className="text-sm text-gray-500 mt-1">Active assignments</p>
                </CardContent>
              </Card>
            </Link>

            {/* Pending Signatures Card */}
            <Link to={data.pending_jsas_count > 0 ? '/my-dashboard#action-required' : '/safety/jsas'} className="block">
              <Card className={`border-l-4 ${data.pending_jsas_count > 0 ? 'border-l-red-500' : 'border-l-green-500'} hover:shadow-md transition-shadow cursor-pointer h-full`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-orange-500" />
                    Pending Signatures
                    {data.pending_jsas_count > 0 && (
                      <Badge variant="destructive" className="ml-auto text-xs">
                        {data.pending_jsas_count}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-gray-900">{data.pending_jsas_count}</p>
                  <p className="text-sm text-gray-500 mt-1">JSAs awaiting signature</p>
                </CardContent>
              </Card>
            </Link>

            {/* My Gear Card */}
            <Link to="/my-dashboard#gear" className="block">
              <Card className="border-l-4 border-l-purple-500 hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Package className="h-4 w-4 text-purple-500" />
                    My Gear
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-gray-900">{data.my_gear_count}</p>
                  <p className="text-sm text-gray-500 mt-1">Items assigned</p>
                </CardContent>
              </Card>
            </Link>

            {/* My Certs Card */}
            <Link to="/my-dashboard#certs" className="block">
              <Card className={`border-l-4 ${data.expiring_certs_count > 0 ? 'border-l-amber-500' : 'border-l-emerald-500'} hover:shadow-md transition-shadow cursor-pointer h-full`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-500" />
                    My Certs
                    {data.expiring_certs_count > 0 && (
                      <Badge className="ml-auto bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs">
                        {data.expiring_certs_count} warning{data.expiring_certs_count !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-gray-900">{data.my_certs_count}</p>
                  <p className="text-sm text-gray-500 mt-1">Certifications</p>
                </CardContent>
              </Card>
            </Link>
          </div>

          {/* Action Required Section */}
          {data.pending_jsas_count > 0 && (
            <Card id="action-required">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="h-5 w-5" />
                  Action Required
                </CardTitle>
                <CardDescription>
                  You have {data.pending_jsas_count} JSA{data.pending_jsas_count !== 1 ? 's' : ''} awaiting your signature
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {data.pending_jsas.map((jsa) => (
                  <PendingJSAItem key={jsa.id} jsa={jsa} />
                ))}
              </CardContent>
            </Card>
          )}

          {/* H3Kit Job Packets — field companion */}
          <JobPacketsSection />

          {/* Travel itineraries */}
          <TravelItinerariesSection />

          {/* Compliance standing — QP-300 clause 2.4 (read-only) */}
          <ComplianceStandingSection />

          {/* My Projects Section */}
          <Card id="projects">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-blue-500" />
                My Projects
              </CardTitle>
              <CardDescription>Your current project assignments</CardDescription>
            </CardHeader>
            <CardContent>
              {data.my_jobs.length > 0 ? (
                <div className="space-y-3">
                  {data.my_jobs.map((job) => (
                    <JobItem key={job.id} job={job} />
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">No active project assignments</div>
              )}
            </CardContent>
          </Card>

          {/* My Certifications Section */}
          <Card id="certs">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-500" />
                My Certifications
              </CardTitle>
              <CardDescription>Your certification status and expiry dates</CardDescription>
            </CardHeader>
            <CardContent>
              {data.my_certs.length > 0 ? (
                <div className="space-y-3">
                  {data.my_certs.map((cert) => (
                    <CertItem key={cert.id} cert={cert} />
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">No certifications on file</div>
              )}
            </CardContent>
          </Card>

          {/* Notifications Section */}
          <Card id="notifications">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-blue-500" />
                Notifications
                {unreadNotifications.length > 0 && (
                  <Badge variant="destructive" className="ml-auto text-xs">
                    {unreadNotifications.length} new
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>Your recent alerts and updates</CardDescription>
            </CardHeader>
            <CardContent>
              {notificationsLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : notifications.length > 0 ? (
                <div className="space-y-2">
                  {notifications.slice(0, 10).map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => handleNotificationClick(n.link, n.id)}
                      className={`w-full text-left p-3 border rounded-lg transition-colors ${
                        n.is_read ? 'bg-white dark:bg-card hover:bg-gray-50 dark:hover:bg-accent' : 'bg-blue-50 dark:bg-blue-950 hover:bg-blue-100 dark:hover:bg-blue-900'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex-shrink-0">
                          {n.is_read ? (
                            <CheckCircle className="h-4 w-4 text-gray-300" />
                          ) : (
                            <Bell className="h-4 w-4 text-blue-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${n.is_read ? 'text-gray-600' : 'font-medium text-gray-900'}`}>
                            {n.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{n.message}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDateTimeInTimeZone(n.created_at, orgTimeZone)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">No notifications</div>
              )}
            </CardContent>
          </Card>

          {/* My Gear Section (collapsed view) */}
          {data.my_gear.length > 0 && (
            <Card id="gear">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-purple-500" />
                  My Gear
                </CardTitle>
                <CardDescription>{data.my_gear_count} item{data.my_gear_count !== 1 ? 's' : ''} assigned to you</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {data.my_gear.map((gear) => (
                    <div key={gear.id} className="flex items-center gap-3 p-3 border rounded-lg">
                      <Package className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{gear.asset_name}</p>
                        <p className="text-sm text-gray-500">
                          {gear.asset_type} &middot; S/N: {gear.asset_serial_number}
                        </p>
                      </div>
                      {gear.due_date && (
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          Due: {formatDateOnly(gear.due_date)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : null}
    </div>
  );
}
